import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardMedia, CardContent, CardActions, CircularProgress, Chip, Grid
} from '@mui/material';
import { getAllTeams, getMyTeams, joinTeam, getSkiRequests } from '../api';
import GroupIcon from '@mui/icons-material/Group';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TeamDetailView from './TeamDetailView';

function TeamCard({ team, onJoin, isMember, status, pendingRequests, onClick }) {
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Card sx={{
        width: '100%', 
        minWidth: 280,
        maxWidth: 280,
        minHeight: 320,
        height: '100%',
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        cursor: onClick ? 'pointer' : 'default', 
        position: 'relative',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }} onClick={onClick}>
        {team.image_url ? (
          <CardMedia component="img" height="140" image={team.image_url} alt={team.name} />
        ) : (
          <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
            <GroupIcon sx={{ fontSize: 60, color: '#bbb' }} />
          </Box>
        )}
        <CardContent>
          <Typography variant="h6" noWrap>{team.name}</Typography>
          {team.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, minHeight: 40, maxHeight: 40, overflow: 'scroll' }}>{team.description}</Typography>}
        </CardContent>
        <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
          {isMember ? (
            status === 'approved' ? (
              <Chip label="Approved" color="success" />
            ) : (
              <Chip label="Requested" color="warning" />
            )
          ) : (
            <Button variant="contained" onClick={e => { e.stopPropagation(); onJoin(team.id); }} fullWidth>Join Team</Button>
          )}
        </CardActions>
      </Card>
    </Box>
  );
}

export default function TeamsSection() {
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [myTeams, setMyTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState({});
  const [pendingRequestsMap, setPendingRequestsMap] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Function to update pending requests for a specific team
  const updatePendingRequestsForTeam = async (teamId) => {
    try {
      const requests = await getSkiRequests(teamId);
      const today = new Date().toISOString().split('T')[0];
      const pending = requests.filter(r => {
        return r.date >= today && (!r.submissions || r.submissions.length === 0);
      }).length;
      setPendingRequestsMap(prev => ({ ...prev, [teamId]: pending }));
    } catch (error) {
      console.warn(`Failed to update pending requests for team ${teamId}:`, error);
      setPendingRequestsMap(prev => ({ ...prev, [teamId]: 0 }));
    }
  };

  const fetchTeams = async (searchTerm = '') => {
    setLoading(true);
    const [my, all] = await Promise.all([
      getMyTeams(),
      getAllTeams(searchTerm)
    ]);
    setMyTeams(my);
    setAllTeams(all);
    setLoading(false);
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    // Fetch pending requests for approved teams
    myTeams.forEach(team => {
      if (team.status === 'approved') {
        updatePendingRequestsForTeam(team.id);
      }
    });
  }, [myTeams]);

  const handleSearch = e => {
    e.preventDefault();
    setSearch(searchValue);
    fetchTeams(searchValue);
  };

  const handleJoin = async (teamId) => {
    setJoining(j => ({ ...j, [teamId]: true }));
    try {
      await joinTeam(teamId);
      fetchTeams(search);
    } finally {
      setJoining(j => ({ ...j, [teamId]: false }));
    }
  };

  // Teams the user is not a member of
  const myTeamIds = new Set(myTeams.map(t => t.id));
  const suggestedTeams = allTeams.filter(t => !myTeamIds.has(t.id));

  if (selectedTeam) {
    return <TeamDetailView 
      team={selectedTeam} 
      onBack={() => setSelectedTeam(null)} 
      onTeamDeleted={() => {
        setSelectedTeam(null);
        fetchTeams(search);
      }}
      onSkiRequestSubmitted={() => updatePendingRequestsForTeam(selectedTeam.id)}
    />;
  }

  return (
    <Box sx={{ width: '100%', px: 2, mt: 4 }}>
      <form onSubmit={handleSearch} style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
        <TextField
          label="Search Teams"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          variant="outlined"
          sx={{ width: 400, mr: 2 }}
        />
        <Button type="submit" variant="contained">Search</Button>
      </form>
      <Typography variant="h5" sx={{ mb: 2 }}>My Teams</Typography>
      {loading ? <CircularProgress /> : (
        <Box sx={{ mb: 4 }}>
          {myTeams.length === 0 ? (
            <Typography sx={{ ml: 2 }}>You are not a member of any teams yet.</Typography>
          ) : isMobile ? (
            // Mobile: Column layout
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {myTeams.map(team => (
                <Box key={team.id} sx={{ mb: 2, width: '100%', maxWidth: 320 }}>
                  <TeamCard team={team} isMember status={team.status} pendingRequests={pendingRequestsMap[team.id] || 0} onClick={() => setSelectedTeam(team)} />
                </Box>
              ))}
            </Box>
          ) : (
            // Desktop: Grid layout
            <Grid container spacing={3}>
              {myTeams.map(team => (
                <Grid item key={team.id} xs={12} sm={6} md={4} lg={3}>
                  <TeamCard team={team} isMember status={team.status} pendingRequests={pendingRequestsMap[team.id] || 0} onClick={() => setSelectedTeam(team)} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      <Typography variant="h5" sx={{ mb: 2 }}>Suggested Teams</Typography>
      {loading ? <CircularProgress /> : (
        <Box sx={{ mb: 4 }}>
          {suggestedTeams.length === 0 ? (
            <Typography sx={{ ml: 2 }}>No suggested teams found.</Typography>
          ) : isMobile ? (
            // Mobile: Column layout
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {suggestedTeams.map(team => (
                <Box key={team.id} sx={{ mb: 2, width: '100%', maxWidth: 320 }}>
                  <TeamCard team={team} onJoin={handleJoin} isMember={false} joining={joining[team.id]} />
                </Box>
              ))}
            </Box>
          ) : (
            // Desktop: Grid layout
            <Grid container spacing={3}>
              {suggestedTeams.map(team => (
                <Grid item key={team.id} xs={12} sm={6} md={4} lg={3}>
                  <TeamCard team={team} onJoin={handleJoin} isMember={false} joining={joining[team.id]} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
} 